import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { detectRegion } from "@/lib/region";
import { TradeReel, TRADE_REEL_MS } from "@/components/landing/TradeReel";
import { LivePortfolioScreen } from "@/components/landing/LivePortfolioScreen";
import { StoreBadges } from "@/components/landing/StoreBadges";
import { Lockup } from "@/components/nav/Brand";
import {
  Moon, ShieldCheck, Coins, Ban, Play, Wifi, Home, PieChart, CandlestickChart,
  Globe2, Wallet as WalletIcon, Check,
} from "lucide-react";

// Fill these in when the builds are published and the badges become real links.
const APP_STORE_URL = "";
const PLAY_STORE_URL = "";

const ScreenTitle = ({ children, chip }) => (
  <div className="flex items-center justify-between">
    <span className="text-[12px] font-semibold tracking-tight text-[#1A1330]">{children}</span>
    {chip}
  </div>
);

const HalalScreen = () => (
  <div className="px-3.5 pt-3">
    <ScreenTitle chip={
      <span className="inline-flex items-center gap-1 rounded-full bg-[#EFF6D8] px-2 py-[3px] text-[9px] font-bold uppercase tracking-wide text-[#3E2E65]">
        <Moon className="h-2.5 w-2.5" />Halal on
      </span>
    }>Screening 40+ names</ScreenTitle>

    <div className="mt-2.5 overflow-hidden rounded-2xl bg-white shadow-[0_2px_10px_-4px_rgba(26,19,48,0.18)] ring-1 ring-black/5">
      <div className="flex items-start gap-2.5 p-3">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-red-500/10">
          <Ban className="h-3.5 w-3.5 text-red-500" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between">
            <span className="text-[12px] font-semibold text-[#1A1330]">JPMorgan Chase</span>
            <span className="text-[9px] font-bold uppercase tracking-wide text-red-500">Blocked</span>
          </span>
          <span className="mt-1 block text-[10px] leading-relaxed text-slate-500">
            Conventional banking revenue — not permissible under AAOIFI screening.
          </span>
        </span>
      </div>
    </div>

    <div className="mt-2 overflow-hidden rounded-2xl bg-white shadow-[0_2px_10px_-4px_rgba(26,19,48,0.18)] ring-1 ring-black/5">
      <div className="p-3">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-emerald-500/10">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            </span>
            <span className="text-[12px] font-semibold text-[#1A1330]">Apple Inc.</span>
          </span>
          <span className="text-[9px] font-bold uppercase tracking-wide text-emerald-700">Compliant</span>
        </div>
        <div className="mt-2.5 space-y-1.5">
          {[["Debt ratio", "under 30%", "1.86%"], ["Non-permissible income", "under 5%", "0.00%"],
            ["Tangible assets", "over 20%", "76.4%"]].map(([label, limit, value]) => (
            <div key={label} className="flex items-center gap-2">
              <span className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full bg-emerald-500/12">
                <Check className="h-2.5 w-2.5 text-emerald-600" strokeWidth={3.5} />
              </span>
              <span className="min-w-0 flex-1 truncate text-[10px] text-slate-500">
                {label} <span className="text-slate-400">· {limit}</span>
              </span>
              <span className="numeric text-[10px] font-semibold text-emerald-600">{value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-[#F7F9EF] px-3 py-1.5 text-[8.5px] text-[#5C7020]">
        Live filings · reviewed by the Tharwy Shariah board
      </div>
    </div>
  </div>
);

const WalletScreen = ({ region }) => {
  const tiles = [[region.currency, region.balance], ["USD", "228,211.65"],
    ["EUR", "12,480.00"], ["GBP", "1,250.00"]].filter(
    (t, idx, arr) => arr.findIndex((x) => x[0] === t[0]) === idx).slice(0, 4);
  return (
    <div className="px-3.5 pt-3">
      <ScreenTitle chip={
        <span className="text-[9px] font-medium text-[color:var(--tharwy-lime-700)]">99 currencies</span>
      }>Your wallet</ScreenTitle>
      <div className="mt-2.5 grid grid-cols-2 gap-2">
        {tiles.map(([c, v]) => {
          const home = c === region.currency;
          return (
            <div key={c} className={`rounded-2xl p-2.5 ring-1 ${
              home ? "bg-[#FBFDF3] ring-[color:var(--tharwy-lime)]/60" : "bg-white ring-black/5"}`}>
              <div className="flex items-center gap-1 text-[9.5px] text-slate-400">
                {c}{home && <span className="rounded-full bg-[#EFF6D8] px-1 py-[1px] text-[8px] font-bold text-[#5C7020]">HOME</span>}
              </div>
              <div className="numeric mt-0.5 text-[13px] font-bold text-[#1A1330]">{v}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-2.5 rounded-2xl bg-white p-3 ring-1 ring-black/5">
        <div className="flex items-center gap-2 text-[11px] font-semibold text-[#1A1330]">
          <span className="grid h-6 w-6 place-items-center rounded-full bg-[#EFF6D8]">
            <Coins className="h-3.5 w-3.5 text-[color:var(--tharwy-lime-700)]" />
          </span>
          Collect locally
        </div>
        <p className="mt-1.5 text-[10px] leading-relaxed text-slate-500">
          Get paid into your own {region.currency} account details on {region.rail} — no intermediary fees.
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
          {region.rails.map((r) => (
            <span key={r} className="rounded-full bg-[#F5F5F2] px-2 py-0.5 text-[9px] font-medium text-slate-600">{r}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

const SCREENS = [
  { key: "trade", label: "A trade, screened and placed", hint: "AAOIFI checks on live filings", dwell: TRADE_REEL_MS, rec: true },
  { key: "portfolio", label: "Live portfolio", hint: "Balances that move as markets do", dwell: 4200 },
  { key: "halal", label: "Halal screening", hint: "Blocked and compliant, side by side", dwell: 4600 },
  { key: "wallet", label: "Multi-currency wallet", hint: "Local rails in 99 currencies", dwell: 4600 },
];

const TABS = [["Home", Home], ["Invest", PieChart], ["Trade", CandlestickChart],
  ["Markets", Globe2], ["Wallet", WalletIcon]];

/** Tilted phone with the app playing inside it — the landing page's product demo. */
export const PhoneShowcase = ({ onCta }) => {
  const [i, setI] = useState(0);
  const [caption, setCaption] = useState("");
  const region = useMemo(() => detectRegion(), []);

  useEffect(() => {
    const t = setTimeout(() => setI((n) => (n + 1) % SCREENS.length), SCREENS[i].dwell);
    return () => clearTimeout(t);
  }, [i]);

  const nodes = {
    trade: <TradeReel onCaption={setCaption} />,
    portfolio: <LivePortfolioScreen />,
    halal: <HalalScreen />,
    wallet: <WalletScreen region={region} />,
  };
  const current = SCREENS[i];
  const activeTab = { trade: "Trade", portfolio: "Home", halal: "Markets", wallet: "Wallet" }[current.key];

  return (
    <section className="bg-[#332654] text-white overflow-hidden" data-testid="phone-showcase">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/80">
            <Play className="h-3 w-3 text-[color:var(--tharwy-lime)]" /> See it in action
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight">
            Your whole portfolio,<br />in your pocket.
          </h2>
          <p className="mt-4 max-w-lg text-white/70">
            Watch a trade get screened against AAOIFI rules and placed in seconds — then hold what
            you make in {region.currency}, the currency you actually live in.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-3 py-1.5 text-[11px] text-white/70"
            data-testid="phone-region-hint">
            <Coins className="h-3 w-3 text-[color:var(--tharwy-lime)]" />
            Set up for {region.label} · {region.currency} on {region.rail}
          </div>

          <div className="mt-6 space-y-2" data-testid="phone-showcase-steps">
            {SCREENS.map((s, idx) => {
              const on = i === idx;
              return (
                <button key={s.key} onClick={() => setI(idx)} data-testid={`phone-step-${s.key}`}
                  className={`group relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border px-4 py-3 text-start
                    transition-colors duration-300 ${on
                      ? "border-[color:var(--tharwy-lime)]/45 bg-white/[0.09]"
                      : "border-white/10 hover:border-white/25 hover:bg-white/5"}`}>
                  <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold transition-colors ${
                    on ? "bg-[color:var(--tharwy-lime)] text-[#332654]" : "bg-white/10 text-white/60"}`}>
                    {idx + 1}
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-sm leading-tight ${on ? "font-semibold text-white" : "text-white/75"}`}>
                      {s.label}
                    </span>
                    <span className={`mt-0.5 block truncate text-[11px] transition-all ${
                      on ? "text-white/55" : "text-white/35"}`}>{s.hint}</span>
                  </span>
                  {on && (
                    <span key={`bar-${i}`} className="absolute bottom-0 start-0 h-[2px] bg-[color:var(--tharwy-lime)] step-progress"
                      style={{ "--step-ms": `${s.dwell}ms` }} />
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button className="rounded-full px-6 h-11 bg-[color:var(--tharwy-lime)] text-[#332654] hover:bg-white"
              onClick={onCta} data-testid="phone-showcase-cta">
              Open free account
            </Button>
          </div>
          <div className="mt-4">
            <StoreBadges appStoreUrl={APP_STORE_URL} playStoreUrl={PLAY_STORE_URL} />
          </div>
          <p className="mt-2.5 text-[11px] text-white/45">
            {APP_STORE_URL || PLAY_STORE_URL
              ? "Face ID sign-in · iOS 15+ and Android 10+"
              : "Mobile builds are in store review — trade in the browser today, Face ID included."}
          </p>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2 flex justify-center">
          <div className="relative" style={{ transform: "rotate(-4deg)" }} data-testid="phone-frame">
            <div className="absolute -inset-10 rounded-[3rem] bg-[color:var(--tharwy-lime)]/10 blur-3xl" />

            {/* titanium side buttons */}
            <div className="absolute -left-[3px] top-[104px] h-8 w-[3px] rounded-l bg-gradient-to-b from-[#3E2E65] to-[#2A1F47]" />
            <div className="absolute -left-[3px] top-[148px] h-12 w-[3px] rounded-l bg-gradient-to-b from-[#3E2E65] to-[#2A1F47]" />
            <div className="absolute -left-[3px] top-[204px] h-12 w-[3px] rounded-l bg-gradient-to-b from-[#3E2E65] to-[#2A1F47]" />
            <div className="absolute -right-[3px] top-[168px] h-16 w-[3px] rounded-r bg-gradient-to-b from-[#3E2E65] to-[#2A1F47]" />

            <div className="relative h-[540px] w-[272px] rounded-[2.9rem] p-[3px] bg-gradient-to-b from-[#5A4A84] via-[#2A1F47] to-[#150E28] shadow-[0_60px_120px_-40px_rgba(0,0,0,0.85)]">
              <div className="relative h-full w-full overflow-hidden rounded-[2.7rem] border-[7px] border-[#0B0715] bg-[#F5F3FA]">

                {/* dynamic island */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-[26px] w-[86px] rounded-full bg-[#07040F] z-30 flex items-center justify-end pe-2.5">
                  <span className="h-[7px] w-[7px] rounded-full bg-[#1B1235] ring-1 ring-[#2C2050]" />
                </div>

                {/* status bar */}
                <div className="absolute inset-x-0 top-0 h-9 z-20 flex items-center justify-between px-4 pt-1.5 text-[#332654]">
                  <span className="text-[10px] font-semibold">9:41</span>
                  <span className="flex items-center gap-[3px]">
                    {[4, 6, 8, 10].map((h) => (
                      <span key={h} className="w-[2.5px] rounded-sm bg-[#332654]" style={{ height: h }} />
                    ))}
                    <Wifi className="h-[11px] w-[11px] ms-0.5" strokeWidth={2.5} />
                    <span className="ms-0.5 relative inline-flex items-center">
                      <span className="h-[9px] w-[17px] rounded-[3px] border border-[#332654]/70 p-[1.5px]">
                        <span className="block h-full w-[78%] rounded-[1px] bg-[#332654]" />
                      </span>
                      <span className="ms-[1px] h-[3px] w-[1.5px] rounded-e bg-[#332654]/70" />
                    </span>
                  </span>
                </div>

                {/* in-app header — the real Tharwy lockup */}
                <div className="absolute inset-x-0 top-9 h-11 z-20 flex items-center justify-between px-3.5 bg-white/92 backdrop-blur border-b border-black/[0.06]"
                  data-testid="phone-app-header">
                  <Lockup className="h-[22px] w-auto" />
                  <span className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#EFF6D8] px-1.5 py-[3px] text-[7.5px] font-bold tracking-wide text-[#3E2E65]">
                      <Moon className="h-2.5 w-2.5" />HALAL
                    </span>
                    <span className="h-5 w-5 rounded-full bg-[#332654] text-[7.5px] font-bold text-white flex items-center justify-center">AT</span>
                  </span>
                </div>

                {/* app screens */}
                <div className="absolute inset-x-0 top-20 bottom-14 overflow-hidden text-[#1A1330]">
                  {SCREENS.map((s, idx) => (
                    <div key={s.key} className={`phone-screen ${i === idx ? "is-active" : ""}`}
                      data-testid={`phone-screen-${s.key}`}>
                      {i === idx ? nodes[s.key] : null}
                    </div>
                  ))}
                </div>

                {/* recording strip sits in its own reserved band under the header */}
                {current.rec && (
                  <div className="absolute inset-x-0 top-20 z-20 flex items-center gap-1.5 px-3 pt-1"
                    data-testid="phone-rec-badge">
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#1A1330]/85 px-1.5 py-[3px] text-[8px] font-bold text-white backdrop-blur">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />REC
                    </span>
                    {caption && (
                      <span className="min-w-0 flex-1 truncate rounded-full bg-[#1A1330]/85 px-2 py-[3px] text-[8.5px] text-white/90 backdrop-blur"
                        data-testid="phone-rec-caption">
                        {caption}
                      </span>
                    )}
                  </div>
                )}

                {/* bottom tab bar */}
                <div className="absolute bottom-0 inset-x-0 h-14 border-t border-black/[0.06] bg-white/95 backdrop-blur flex items-start justify-around pt-2 z-10">
                  {TABS.map(([label, Icon]) => {
                    const on = label === activeTab;
                    return (
                      <span key={label} className={`flex flex-col items-center gap-1 transition-colors ${
                        on ? "text-[#332654]" : "text-slate-400"}`}>
                        <span className={`grid h-6 w-6 place-items-center rounded-full transition-colors ${
                          on ? "bg-[#EFF6D8]" : ""}`}>
                          <Icon className="h-[13px] w-[13px]" strokeWidth={on ? 2.6 : 2} />
                        </span>
                        <span className={`text-[7.5px] ${on ? "font-bold" : ""}`}>{label}</span>
                      </span>
                    );
                  })}
                </div>
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 h-[3px] w-24 rounded-full bg-[#1A1330]/25 z-20" />

                {/* screen glare */}
                <div className="pointer-events-none absolute inset-0 z-40 bg-gradient-to-tr from-transparent via-white/10 to-white/20 mix-blend-overlay" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
